import type { ReactNode } from 'react';
import React from 'react';

import { Modal } from '../../../../common/display/modal/Modal';
import LegalInsuranceJpg from './legal-insurance.jpg';
import LegalInsuranceWebp from './legal-insurance.webp';
import DocumentPng from './document.png';
import DocumentWebp from './document.webp';
import ScalePng from './scale.png';
import ScaleWebp from './scale.webp';
import PhonePng from './phone.png';
import PhoneWebp from './phone.webp';
import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { FAQTab } from '../../../../common/display/faqTab/FAQTab';
import { APP_CONFIG } from '@/config';
import { Benefit } from '@/@types/benefits';
import type { BenefitModalProps } from '@/@types/benefitModalProps';
import { Msg, useIntl } from '@/i18n';
import OverviewTab from '@/components/content/benefits/modals/legalInsurance/components/OverviewTab';
import { createTelLinkHandler } from '@/utils/telLinkUtils';

const HowItWorksTab = () => {
  const { t } = useIntl();
  const Item = OrderedList.Item;

  return (
    <div>
      <OrderedList>
        <Item
          image={
            <ImageSource
              image={DocumentPng}
              imageWebp={DocumentWebp}
              imageAlt={t({
                id: 'benefits.legalInsurance.modal.howItWorks.image1',
                defaultMessage: 'document',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.legalInsurance.modal.howItWorks.paragraph1',
            defaultMessage: 'Get your documents after activation',
          })}
        />
        <Item
          image={
            <ImageSource
              image={ScalePng}
              imageWebp={ScaleWebp}
              imageAlt={t({
                id: 'benefits.legalInsurance.modal.howItWorks.image2',
                defaultMessage: 'Scale',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.legalInsurance.modal.howItWorks.paragraph2',
            defaultMessage: 'Wait for three months',
          })}
        />
        <Item
          image={
            <ImageSource
              image={PhonePng}
              imageWebp={PhoneWebp}
              imageAlt={t({
                id: 'benefits.legalInsurance.modal.howItWorks.image3',
                defaultMessage: 'Phone',
              })}
              sizes='100px'
            />
          }
          text={
            <Msg
              id='benefits.legalInsurance.modal.howItWorks.paragraph3'
              defaultMessage='Contact insurance in case of need'
              values={{
                PhoneNumber: (
                  <a
                    href={`tel:${t({
                      id: 'benefits.legalInsurance.modal.howItWorks.phoneNumber',
                      defaultMessage: '555',
                    }).replaceAll(/[^0-9]*/g, '')}`}
                    onClick={createTelLinkHandler(
                      t({
                        id: 'benefits.legalInsurance.modal.howItWorks.phoneNumber',
                        defaultMessage: '555',
                      }),
                    )}
                  >
                    <Msg id='benefits.legalInsurance.modal.howItWorks.phoneNumber' defaultMessage='555' />
                  </a>
                ),
                EMail: (
                  <a
                    href={`mailto:${encodeURIComponent(
                      t({
                        id: 'benefits.legalInsurance.modal.howItWorks.eMail',
                        defaultMessage: 'E-Mail',
                      }),
                    )}`}
                  >
                    <Msg id='benefits.legalInsurance.modal.howItWorks.eMail' defaultMessage='E-Mail' />
                  </a>
                ),
                Br: <br />,
              }}
            />
          }
        />
      </OrderedList>
    </div>
  );
};

const buttonArea = (
  <div className='align-center'>
    <a className='button-secondary button-rounded' href={`${APP_CONFIG.apiEndpoint}/legal-insurance/my/download`}>
      <Msg id='benefits.legalInsurance.modal.details.downloadDocument' defaultMessage='Download insurance' />
    </a>
  </div>
);

export const LegalInsurance = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();

  return (
    <Modal
      title={t({ id: 'benefits.legalInsurance.modal.headline', defaultMessage: 'Legal insurance' })}
      isVisible={isVisible}
      image={LegalInsuranceJpg}
      imageWebp={LegalInsuranceWebp}
      imageAlt={t({
        id: 'benefits.legalInsurance.modal.image',
        defaultMessage: 'New build in sunshine',
      })}
      imageUseFullWidth={true}
      tabsContent={[
        <OverviewTab key='benefits.legalInsurance.modal.overview' />,
        <HowItWorksTab key='benefits.legalInsurance.modal.howItWorks' />,
        <FAQTab
          key='benefits.legalInsurance.modal.faq'
          benefit='legalInsurance'
          values={{
            insuranceAgb: (children: ReactNode[]) => (
              <a href={`${APP_CONFIG.apiEndpoint}/legal-insurance/conditions`} target='_blank' rel='noreferrer'>
                {children}
              </a>
            ),
          }}
        />,
      ]}
      buttonArea={buttonArea}
      benefit={Benefit.LEGAL_INSURANCE}
      onClose={onClose}
    />
  );
};
